import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api-client.js';
import { ApiError, ensureOk } from '../lib/api-error.js';
import { toolHistoryUserId, type AskHistoryResult, type HistoryKind, type HistoryPage, type WhenHistoryResult } from '../lib/tool-history.js';
import { useAuth } from '../store/auth.js';

/** Lists and details are isolated by the authenticated owner and tool. Reads never mutate. */
export function useToolHistory<K extends HistoryKind>(kind: K, listEnabled = true) {
  const jwt = useAuth(s => s.jwt);
  const knownUserId = useAuth(s => s.user?.id);
  const userId = knownUserId ?? toolHistoryUserId(jwt);
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const resultId = params.get('result');
  const scope = ['tool-history', userId, kind] as const;
  const enabled = !!jwt && !!userId;
  const list = useInfiniteQuery({
    queryKey: [...scope, 'list'],
    enabled: enabled && listEnabled,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const suffix = pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : '';
      const res = await ensureOk(await apiFetch(`/api/v1/history/${kind}${suffix}`, jwt!));
      return await res.json() as HistoryPage;
    },
    getNextPageParam: page => page.nextCursor,
  });
  type Detail = K extends 'ask' ? AskHistoryResult : K extends 'when' ? WhenHistoryResult : Record<string, unknown>;
  const detail = useQuery<Detail, Error>({
    queryKey: [...scope, 'detail', resultId],
    enabled: enabled && resultId !== null,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    queryFn: async () => {
      if (!resultId || resultId.length > 200) throw new ApiError(404, 'not_found', {});
      const res = await ensureOk(await apiFetch(`/api/v1/history/${kind}/${encodeURIComponent(resultId!)}`, jwt!));
      return await res.json() as Detail;
    },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (!enabled) throw new Error('not_authenticated');
      try {
        await ensureOk(await apiFetch(`/api/v1/history/${kind}/${encodeURIComponent(id)}`, jwt!, { method: 'DELETE' }));
      } catch (error) {
        if (!(error instanceof ApiError && error.status === 404)) throw error;
      }
    },
    onSuccess: (_data, id) => {
      client.removeQueries({ queryKey: [...scope, 'detail', id], exact: true });
      client.setQueryData<InfiniteData<HistoryPage>>([...scope, 'list'], old => old && ({ ...old, pages: old.pages.map(page => ({ ...page, items: page.items.filter(row => row.id !== id) })) }));
      void client.invalidateQueries({ queryKey: [...scope, 'list'] });
    },
  });
  function open(id: string | null, replace = false): void {
    setParams(current => {
      const next = new URLSearchParams(current);
      if (id === null) next.delete('result'); else next.set('result', id);
      return next;
    }, { replace });
  }
  return { ...list, items: enabled ? list.data?.pages.flatMap(page => page.items) ?? [] : [], detail, resultId, open, remove, userId };
}
